import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '@/services/apiFetch'
import { formatSheetAmount } from '@/lib/sheetAmount'
import { displayCell } from '@/lib/cellDisplay'
import { ExcelEditableTable, type ExcelColumn } from '@/components/table/ExcelEditableTable'

/** Một dòng giá ACTIVE của NCC. */
export type ActiveRuleOption = {
  id: string
  quote_id: string
  quote_title: string | null
  item_id: string | null
  item_name?: string | null
  item_code?: string | null
  item_attributes?: Record<string, unknown> | null
  item_uom?: string | null
  item_group_code?: string | null
  supplier_id?: string
  supplier_name?: string
  supplier_item_name: string
  uom: string
  unit_price: number
  spec: string | null
  density: string | null
  /** Dòng gốc trên báo giá (không còn hiển thị; giữ để truy vết). */
  original_row_ref: string | null
  /** Ánh xạ chỉ đọc của dòng theo tên cột (KT1…, Định lượng, ĐV đơn giá). */
  mappings?: Record<string, unknown>
  /** Cột hiện trên PO của báo giá, theo thứ tự. */
  po_columns?: string[]
  /** Đơn vị giá của dòng (m³/mét/cái). Không có thì không ghi đơn vị. */
  sheet_unit?: string | null
  /** Cột nhập lúc mua (ô gõ số) của dòng bảng tính. Rỗng với dòng kiểu cũ. */
  input_names?: string[]
  is_sheet_backed?: boolean
}

/** Số dòng mỗi lần tải từ máy chủ (tối đa của máy chủ). */
export const ACTIVE_RULES_PAGE_SIZE = 200
/** Giới hạn số trang khi tải đủ dòng của vật tư (an toàn, vật tư thường có vài trăm dòng). */
const MAX_PAGES = 10

type Props = {
  supplierId: string
  /** Vật tư của dòng PO; để trống thì không lọc theo vật tư. */
  itemId: string | null
  selectedRuleId: string | null
  onSelect: (rule: ActiveRuleOption) => void
  onClose: () => void
  sheetOnly?: boolean
  initialSearch?: string
}

/** Cột KT theo thứ tự xuất hiện trong các dòng (theo cột hiện trên PO). */
export function sheetColumnsOf(rules: ActiveRuleOption[]): string[] {
  const out: string[] = []
  for (const r of rules) {
    for (const name of r.po_columns ?? []) {
      if (!out.includes(name)) out.push(name)
    }
  }
  return out
}

/** Giá kèm đơn vị giá của dòng: "5.525 đ/mét" với dòng có đơn vị; "4.558 đ" nếu không có. */
export function priceLabelOf(r: Pick<ActiveRuleOption, 'unit_price' | 'sheet_unit'>): string {
  const amount = formatSheetAmount(r.unit_price)
  if (!amount) return ''
  return r.sheet_unit ? `${amount} đ/${r.sheet_unit}` : `${amount} đ`
}

/** Dòng của bảng: phẳng để bảng lọc và sắp được; __rule giữ dòng gốc. */
type PickerRow = Record<string, unknown> & { __id: string; __rule: ActiveRuleOption }

/** Tải đủ dòng ACTIVE của vật tư (theo trang của máy chủ). */
async function loadAllRules(supplierId: string, itemId: string | null, initialSearch?: string): Promise<{ rules: ActiveRuleOption[]; total: number }> {
  const out: ActiveRuleOption[] = []
  let total = 0
  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({ limit: String(ACTIVE_RULES_PAGE_SIZE), offset: String(page * ACTIVE_RULES_PAGE_SIZE) })
    if (itemId) params.set('item_id', itemId)
    if (initialSearch) params.set('q', initialSearch)
    const res = await apiFetch(`/api/suppliers/${encodeURIComponent(supplierId)}/active-rules?${params.toString()}`)
    const body = (await res.json()) as { ok?: boolean; rules?: ActiveRuleOption[]; total?: number; error?: { message?: string } }
    if (!res.ok || !body.ok) throw new Error(body.error?.message ?? 'Không tải được danh sách báo giá')
    total = body.total ?? 0
    out.push(...(body.rules ?? []))
    if (out.length >= total || (body.rules ?? []).length === 0) break
  }
  return { rules: out, total }
}

/**
 * Chọn dòng giá ACTIVE của NCC cho dòng PO. Tải đủ dòng của vật tư rồi lọc và sắp ngay trên bảng
 * (cùng cơ chế phễu lọc của ExcelEditableTable), nên lọc chạy trên toàn bộ dòng, không chỉ trang đang xem.
 */
export function PoRulePickerModal({ supplierId, itemId, selectedRuleId, onSelect, onClose, sheetOnly = false, initialSearch = '' }: Props) {
  const [rules, setRules] = useState<ActiveRuleOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Esc đóng hộp thoại
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    loadAllRules(supplierId, itemId, initialSearch)
      .then(({ rules: loaded }) => { if (!cancelled) setRules(sheetOnly ? loaded.filter((rule) => rule.is_sheet_backed) : loaded) })
      .catch((err: unknown) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Không tải được danh sách báo giá') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [supplierId, itemId, sheetOnly, initialSearch])

  const sheetColumns = useMemo(() => sheetColumnsOf(rules), [rules])
  const isSheetList = sheetColumns.length > 0

  const rows = useMemo<PickerRow[]>(() => rules.map((r) => {
    const row: PickerRow = {
      __id: r.id,
      __rule: r,
      name: r.supplier_item_name,
      price: Number(r.unit_price),
      specText: [r.spec, r.density].filter(Boolean).join(' · '),
    }
    for (const col of sheetColumns) row[col] = displayCell(r.mappings?.[col])
    return row
  }), [rules, sheetColumns])

  const columns = useMemo<ExcelColumn<PickerRow>[]>(() => {
    const stt: ExcelColumn<PickerRow> = {
      key: '__stt',
      label: 'STT',
      width: 50,
      align: 'center',
      readOnly: true,
      sortable: false,
      filterable: false,
      formatter: (_val, _row, index) => String(index + 1),
    }
    const name: ExcelColumn<PickerRow> = { key: 'name', label: 'Tên hàng trong báo giá', width: 240, align: 'left', readOnly: true, sortable: true, filterable: true }
    const kt: ExcelColumn<PickerRow>[] = isSheetList
      ? sheetColumns.map((c) => ({ key: c, label: c, width: 110, align: 'right', readOnly: true, sortable: true, filterable: true }))
      : [{ key: 'specText', label: 'Quy cách', width: 180, align: 'left', readOnly: true, sortable: true, filterable: true }]
    const price: ExcelColumn<PickerRow> = {
      key: 'price',
      label: 'Đơn giá',
      width: 150,
      align: 'right',
      type: 'currency',
      readOnly: true,
      sortable: true,
      filterable: true,
      formatter: (_val, row) => priceLabelOf(row.__rule),
    }
    const choose: ExcelColumn<PickerRow> = {
      key: '__choose',
      label: '',
      width: 80,
      align: 'center',
      readOnly: true,
      sortable: false,
      filterable: false,
      renderCell: (_val, row) => (
        <button type="button" className="text-blue-700 underline" onClick={() => onSelect(row.__rule)}>Chọn</button>
      ),
    }
    return [stt, name, ...kt, price, choose]
  }, [isSheetList, sheetColumns, onSelect])

  return (
    <div className="flex max-h-[85vh] w-full flex-col">
      <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
        <span className="text-sm text-slate-600">
          {loading ? 'Đang tải dòng báo giá…' : `${rows.length} dòng ACTIVE của vật tư này. Bấm phễu ở tiêu đề cột để lọc, mũi tên để sắp.`}
        </span>
        <button type="button" className="rounded border px-3 py-1 text-sm" onClick={onClose}>Đóng</button>
      </div>

      {error && <div className="mb-2 shrink-0 text-sm text-red-700">{error}</div>}

      {!loading && !error && (
        <div className="min-h-0 flex-1 overflow-auto">
        <ExcelEditableTable<PickerRow>
          columns={columns}
          data={rows}
          onChange={() => undefined}
          emptyRowFactory={() => ({ __id: '', __rule: {} as ActiveRuleOption })}
          rowKey={(row) => row.__id}
          readOnly
          showActions={false}
          showAddRowButton={false}
          emptyMessage="Không có dòng báo giá ACTIVE nào"
          getRowClassName={(row) => (row.__id === selectedRuleId ? 'bg-emerald-50' : '')}
        />
        </div>
      )}
    </div>
  )
}
